import { Alert, Layout, Row, Space, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AddCircleGrey from "../../../assets/icons/add-circle-grey.svg";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import FormationModalComponent from "../../../components/Formations/Creators/Modals";
import { REQUEST_LOADING_STATES } from "../../../helpers/constant";
import { usePreviousLocationContext } from "../../../helpers/PreviousLocationContext";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import { getQuickFileAlbum, getQuickFilesSize } from "../../../redux/quickFile/quickFileSlice";
import { AppDispatch, StoreState } from "../../../redux/store";
import { updateTutorialVisibilityStatus } from "../../../redux/users/usersSlice";
import CreatorAddMediaComponent from "../CreatorAddMediaComponent";
import CreatorCreateAudioComponent from "../CreatorCreateAudioComponent";
import CreatorCreateNewAlbumComponent from "../CreatorCreateNewAlbumComponent";
import "./index.less";
import QuickFileList from "./QuickFileList";
import QuickFileTab, { QuickFileTabValue } from "./QuickFileTab/QuickFileTab";

const CreatorQuickFileComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation = usePreviousLocationContext();
  const shouldFetch = useRef(true);
  const [queryParams, setQueryParams] = useState({
    id: "",
    conversationId: "",
    messageId: "",
    receiverId: "",
    collectionTitle: "",
    block: "",
    scriptId: "",
  });
  const dispatch = useDispatch<AppDispatch>();
  const { list: quickFileAlbumList, folderSize } = useSelector((state: StoreState) => state.quickFile);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [albumIdToDelete, setAlbumIdToDelete] = useState<string>("");
  const { currentUserSettings } = useFetchUserSettings();
  const [activeTab, setActiveTab] = useState<QuickFileTabValue>(() => {
    // Check if audioOnly mode is enabled in localStorage
    const audioOnly = localStorage.getItem("quickFileAudioOnly") === "true";
    const mediaOnly = localStorage.getItem("quickFileMediaOnly") === "true";
    if (audioOnly) {
      return "audio";
    }
    if (mediaOnly) {
      return "media";
    }
    const savedTab = localStorage.getItem("quickFileActiveTab");
    return savedTab === "media" || savedTab === "audio" ? savedTab : "media";
  });
  const [disableMediaTab, setDisableMediaTab] = useState<boolean>(() => {
    return localStorage.getItem("quickFileAudioOnly") === "true";
  });
  const [disableAudioTab, setDisableAudioTab] = useState<boolean>(() => {
    return localStorage.getItem("quickFileMediaOnly") === "true";
  });
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null);
  const [creatingAlbum, setCreatingAlbum] = useState<boolean>(false);
  const [addingMediaAlbumId, setAddingMediaAlbumId] = useState<string | null>(null);
  const [addMediaOrigin, setAddMediaOrigin] = useState<"create" | "album" | null>(null);
  const [creatingAudio, setCreatingAudio] = useState<boolean>(false);
  const [creatingAudioAlbumId, setCreatingAudioAlbumId] = useState<string | null>(null);

  useEffect(() => {
    if (shouldFetch.current) {
      shouldFetch.current = false;
      dispatch(getQuickFileAlbum());
      dispatch(getQuickFilesSize());
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id") || "";
    const conversationId = params.get("conversationId") || "";
    const receiverId = params.get("receiverId") || "";
    const messageId = params.get("messageId") || "";
    const collectionTitle = params.get("collectionTitle") || "";
    const block = params.get("block") || "";
    const scriptId = params.get("scriptId") || "";
    setQueryParams({ id, conversationId, receiverId, messageId, collectionTitle, block, scriptId });
    if (id === "unlock-link" || id === "collection") {
      setDisableAudioTab(true);
    }
  }, [location.search]);

  const handleNavigation = (): string => {
    if (queryParams.id === "collection") {
      return `/creator/create/collection?collectionTitle=${queryParams.collectionTitle}`;
    }
    if (queryParams.id === "post") {
      return "/creator/publication";
    }
    if (queryParams.id === "automation") {
      return `/creator/message-automatic/${queryParams.block}`;
    }
    if (queryParams.id === "chatScripts" && queryParams?.conversationId) {
      return `/creator/chat-scripts/create?conversationId=${queryParams.conversationId}`;
    }
    if (queryParams?.conversationId) {
      if (queryParams.id === "pendingMedia") {
        return `/creator/conversation/${queryParams?.conversationId}`;
      } else if (queryParams.id === "conversation") {
        return previousLocation?.pathname!;
      } else {
        return `/creator/mediaPush?conversation=${true}&conversationId=${queryParams?.conversationId}&receiverId=${queryParams?.receiverId}&messageId=${queryParams?.messageId}`;
      }
    }
    if (["/creator/createNewAlbum"].includes(previousLocation?.pathname!) || previousLocation?.pathname.includes("/creator/AddMedia")) {
      return "/common/setting";
    }
    return "";
  };

  const handleTutorialRead = async () => {
    dispatch(updateTutorialVisibilityStatus("hasSeenQuickFileModal"));
  };

  const handleTabChange = (tab: QuickFileTabValue) => {
    setActiveTab(tab);
    localStorage.setItem("quickFileActiveTab", tab);
  };

  const showingAlbumInline = !!selectedAlbumId;
  const showingCreateInline = creatingAlbum;
  const showingAddMediaInline = !!addingMediaAlbumId;
  const showingCreateAudioInline = creatingAudio;

  if (showingCreateAudioInline) {
    return (
      <CreatorCreateAudioComponent
        inlineMode={true}
        albumId={creatingAudioAlbumId || undefined}
        onBack={() => {
          setCreatingAudio(false);
          setCreatingAudioAlbumId(null);
        }}
        onCreated={(audioFile) => {
          console.log("Audio file created:", audioFile);
          setCreatingAudio(false);
          setCreatingAudioAlbumId(null);
          // Audio upload is now handled within the component
        }}
      />
    );
  }

  if (showingAddMediaInline) {
    return (
      <CreatorAddMediaComponent
      // TODO: Add proper props for inline mode
      />
    );
  }

  if (showingCreateInline) {
    return (
      <CreatorCreateNewAlbumComponent
        inlineMode={true}
        onBack={() => setCreatingAlbum(false)}
        onCreated={(newAlbumId) => {
          setCreatingAlbum(false);
          setAddingMediaAlbumId(newAlbumId);
          setAddMediaOrigin("create");
        }}
      />
    );
  }

  return (
    <Layout>
      <Layout.Content className="CreatorQuickLinkContentWrapper">
        <Row className="relative justify-content-center header-title-container-70">
          <ArrowLeftIconComponent
            left={-2}
            top={10}
            navigateTo={handleNavigation()}
            callback={() => {
              if (queryParams.id === "collection") {
                sessionStorage.removeItem("selected-quick-files");
              }
            }}
          />
          <Typography.Text className="ml-15 header-title-black-35">{t("Quick-Files")}</Typography.Text>
        </Row>
        {!queryParams?.id && (
          <Space className="w-full items-center mt-16" direction="vertical">
            <div>
              <img onClick={() => navigate("/creator/createNewAlbum")} src={AddCircleGrey} width={150} height={150} alt="plus" />
            </div>
            <Typography.Text className="CreateNewAlbum">{t("click-to-create-a-new-album")}</Typography.Text>
          </Space>
        )}

        {folderSize && (
          <Space direction="vertical" className="w-full mt-25">
            <Alert message={t("storage-max-size-warning-message")} type="warning" showIcon closable />
          </Space>
        )}

        <div className="quick-file-box">
          <QuickFileTab onChange={handleTabChange} initialValue={activeTab} disableMediaTab={disableMediaTab} disableAudioTab={disableAudioTab} />

          {activeTab === "media" ? (
            <QuickFileList
              queryParams={queryParams}
              quickFiles={quickFileAlbumList}
              loading={REQUEST_LOADING_STATES.SUCCEEDED}
              filterType="photos-videos"
              onAlbumSelect={undefined}
            />
          ) : (
            <QuickFileList
              queryParams={queryParams}
              quickFiles={quickFileAlbumList}
              loading={REQUEST_LOADING_STATES.SUCCEEDED}
              filterType="audio"
              onAlbumSelect={undefined}
            />
          )}
        </div>
        {currentUserSettings && !currentUserSettings.visibilityStatus.hasSeenQuickFileModal && (
          <FormationModalComponent id="quick-file" visible={true} handleTutorialRead={handleTutorialRead} />
        )}
      </Layout.Content>
    </Layout>
  );
};

export default CreatorQuickFileComponent;
